<template>
  <form class="roof-sides-form">
    <div class="form-group form-group--toggle">
      <label for="unit-switcher">{{ t('general.unit') }}</label>
      <div class="d-flex align-items-center gap-3 mt-1">
        <span @click="switchToM">
          {{ t('general.meter') }}
        </span>
        <label for="unit-switcher" class="toggle-trigger">
          <input
            type="checkbox"
            id="unit-switcher"
            :checked="unitSwitcherIsChecked"
            @change="$emit('on-unit-change', $event.target.checked)"
            class="form-control"
            data-test="roof-side-unit-switcher"
          />
        </label>
        <span @click="switchToCm">
          {{ t('general.centimeter') }}
        </span>
      </div>
    </div>
    <div v-for="(roofSide, index) in roofType.sides" :key="index" class="form-group">
      <label :for="roofSide">
        {{ t(`general.roofSides.${roofSide}`) }}
        ({{ getRoofSideLabel(roofSide) }})
      </label>
      <div class="input-wrapper">
        <input
          v-model.number="validations[roofSide].$model"
          type="number"
          :name="roofSide"
          :id="roofSide"
          class="form-control"
          :class="{ 'is-invalid': validations[roofSide].$error || serverValidation[roofSide] }"
          :placeholder="0"
          data-test="roof-side-input"
          min="0"
          :step="unitSwitcherIsChecked ? 10 : 0.1"
        />
        <div class="input-mirror" aria-hidden="true">
          <span class="input-mirror__value">{{ String(validations[roofSide].$model || 0) }}</span>
          <span class="input-mirror__suffix">{{ unitSwitcherIsChecked ? '&nbsp;cm' : '&nbsp;m' }}</span>
        </div>
      </div>
      <ErrorMessage :field="validations[roofSide]" />
    </div>
    <ServerValidation field="sides" :server-validation="serverValidation" />
  </form>
</template>

<script>
import { defineComponent, computed, reactive, toRefs } from 'vue'
import { useVuelidate } from '@vuelidate/core'
import { required, minValue, maxValue, numeric } from '@vuelidate/validators'
import { useI18n } from 'vue-i18n'

import ServerValidation from '@/components/Global/ServerValidation'
import ErrorMessage from '@/components/Global/ErrorMessage'

export default defineComponent({
  name: 'MeasurementsFields',
  emits: ['on-input', 'on-unit-change'],
  components: {
    ServerValidation,
    ErrorMessage
  },
  props: {
    roofType: {
      type: Object,
      required: true
    },
    serverValidation: {
      type: Object,
      default: () => ({})
    },
    current: {
      type: Object,
      required: true
    },
    unitSwitcherIsChecked: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    function maxDecimalsValidator(value) {
      if (value === null || value === undefined || value === '') return true

      const str = value.toString()
      const decimals = str.split('.')[1]

      return !decimals || decimals.length <= 1
    }

    const { t } = useI18n()

    const sides = computed(() => props.current)
    const rules = computed(() => {
      const ruleSet = {}
      if (!props.roofType.sides) {
        return ruleSet
      }

      const getMaxValueForSide = side => {
        switch (side) {
          case 'side_a':
            return props.unitSwitcherIsChecked ? 6000 : 60
          case 'side_b':
          case 'side_d':
          case 'side_e':
            return props.unitSwitcherIsChecked ? 3000 : 30
        }
      }

      props.roofType.sides.forEach(side => {
        ruleSet[side] = {
          required,
          numeric,
          minValue: minValue(0),
          maxValue: maxValue(getMaxValueForSide(side)),
          maxDecimals: maxDecimalsValidator
        }
      })

      return ruleSet
    })

    const state = reactive({
      validations: null
    })

    state.validations = useVuelidate(rules, sides)

    function getRoofSideLabel(side) {
      return side.split('_')[1].toUpperCase()
    }

    function switchToM() {
      if (!props.unitSwitcherIsChecked) return

      emit('on-unit-change', false)
    }

    function switchToCm() {
      if (props.unitSwitcherIsChecked) return

      emit('on-unit-change', true)
    }

    return {
      getRoofSideLabel,
      ...toRefs(state),
      t,
      switchToM,
      switchToCm
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@/assets/style/base/variables' as *;

.roof-sides-form {
  max-width: 210px;
  margin: 0 auto;

  label {
    span {
      text-transform: lowercase;
      padding: 0 5px;
    }
  }

  input[type='number']::-webkit-inner-spin-button,
  input[type='number']::-webkit-outer-spin-button {
    opacity: 1;
  }
}

.input-wrapper {
  position: relative;

  .input-mirror {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    padding: 15px;
    pointer-events: none;
    font-size: 1rem;

    &__value {
      visibility: hidden;
      white-space: pre;
    }
  }

  &:has(input:focus) .input-mirror__suffix {
    color: $pinkish-gray;
  }
}
</style>
